import "../../styles/global.css";
import "./Overview.css";
import React, { useState, useEffect, use } from "react";
import { fetchDemoSubmissionCount, fetchDemoQuestionAverage, fetchAllDemoSubmissionDates, fetchDemoClickTimeRange } from "../../api/overviewApi";
import GroupedBarChart from "./GroupedBarChart";
import QuestionTable from "./QuestionTable";
import { Slider, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
  
const Overview = () => {
    const [submissionCount, setSubmissionCount] = useState(null);
    const [questionAverage, setQuestionAverage] = useState([]);
    const [sessionInfo, setSessionInfo] = useState(null);
    const [error, setError] = useState(null);
    const [allDates, setAllDates] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);
    const [clickTimeRange, setClickTimeRange] = useState(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');

        console.log("🔑 Token from URL:", token);
      
        if (token) {
          fetch('https://api.olaitools.com/api/dashboard/lti-session-info', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ token })
          })
          .then(res => res.json())
          .then(data => {
            console.log("Decoded user info:", data);
            setSessionInfo(data.user);
          })
          .catch(err => {
            console.error("Failed to fetch user info:", err);
          });
        } else {
            console.error("No token found in URL");
        }
    }, []);

    // useEffect(() => {
    //     const getSessionInfo = async () => {
    //         try {
    //             const data = await fetchLtiSessionInfo();
    //             console.log('LTI session info:', data);
    //             setSessionInfo(data);
    //         } catch (error) {
    //             console.error('Error fetching LTI session info:', error);
    //             setSessionInfo({ name: "Demo Course" });
    //         }   
    //     }
    //     getSessionInfo();
    // }, []);

    // fetch min and max dates for slider
    useEffect(() => {
        console.log("Fetching all demo submission dates...");
        fetchAllDemoSubmissionDates()
            .then((data) => {
                const timestamps = data.dates.map((d) => new Date(d).getTime());
                const minDate = Math.min(...timestamps);
                const maxDate = Math.max(...timestamps);
                setAllDates([minDate, maxDate]);
                setDateRange([minDate, maxDate]);
            })
            .catch((error) => {
                console.error("Error fetching all demo submission dates:", error);
            });
    }, []);

    useEffect(() => {
        console.log("Fetching demo click time range...");
        fetchDemoClickTimeRange()
            .then((data) => {
                console.log("Demo click time range data:", data);
                if (data && data.click_timerange) {
                    setClickTimeRange(data.click_timerange);
                } else {
                    console.warn("No time range returned");
                }
            })
            .catch((err) => {
                console.error("Error fetching demo click time range:", err);
            });
    }, []);

    useEffect(() => {
        console.log("overview.js - allDates, dateRange:", allDates, dateRange);
        if (dateRange[0] && dateRange[1]) {
          const [startISO, endISO] = dateRange.map((ts) =>
            new Date(ts).toISOString()
          );

          console.log("overview.js - startISO, endISO:", startISO, endISO);
    
          fetchDemoSubmissionCount(startISO, endISO)
            .then(setSubmissionCount)
            .catch(() => setError("Failed to load submission count"));
    
          fetchDemoQuestionAverage(startISO, endISO)
            .then((data) => setQuestionAverage(data?.questions ?? []))
            .catch(() => setError("Failed to load question averages"));

        }
    }, [dateRange]);

    const handleSliderChange = (event, newValue) => {
        setDateRange(newValue);
    };

    return (
        <div className="wrapper">
        <h1 className="title">{sessionInfo?.name}</h1>
        <p className="step-overview-1">
            Welcome to iTOOLS! This dashboard provides an overview of student
            engagement and learning activities.
        </p>

        <div className="dashboard-box">

        
            <div className="section-content">
                {/* Left panel: controls */}
                <div className="controls-panel">
                    <Typography gutterBottom>Select your class:</Typography>
                    <select
                        value="DEMO CLASS"
                        style={{
                            width: "100%",
                            padding: "0.5rem",
                            marginBottom: "1rem",
                            borderRadius: "6px",
                            border: "1px solid #ccc",
                            fontSize: "1rem"
                        }}
                        disabled
                    >
                        <option value="DEMO CLASS">DEMO CLASS</option>
                    </select>

                    {allDates.length === 2 && dateRange[0] && dateRange[1] &&(
                        <>
                        <Typography gutterBottom>Select time range of survey submissions:</Typography>
                        <div className ="slider-wrapper">
                            <Slider
                                value={dateRange}
                                onChange={handleSliderChange}
                                valueLabelDisplay="auto"
                                min={allDates[0]}
                                max={allDates[1]}
                                step={24 * 60 * 60 * 1000} // 1 day
                                valueLabelFormat={(ts) => dayjs(ts).format("MMM D")}
                            />
                        </div>
                        </>
                    )}
                </div>

                {/* Right panel: submission count and question averages */}
                <div className="stats-panel">
                    {error ? (
                        <p style={{ color: "red" }}>{error}</p>
                    ) : submissionCount !== null ? (
                
                        <div className="stats-grid">
                            <div className="stats-column">
                                <p><strong>Total Responses</strong><br />{submissionCount.count} submission(s)</p>
                                <p><strong>Survey From</strong><br />{dateRange[0] ? dayjs.utc(dateRange[0]).format("MM/DD/YYYY h:mm A") + " UTC" : "N/A"}</p>
                                <p><strong>Click Data From</strong><br />{clickTimeRange?.start_time ? dayjs.utc(clickTimeRange.start_time).format("MM/DD/YYYY h:mm A") + " UTC" : "N/A"}</p>
                            </div>

                            <div className="stats-column">
                                <p><strong>Total Respondents</strong><br />11 student(s)</p>
                                <p><strong>To</strong><br />{dateRange[1] ? dayjs.utc(dateRange[1]).format("MM/DD/YYYY h:mm A") + " UTC" : "N/A"}</p>
                                <p><strong>To</strong><br />{clickTimeRange?.end_time ? dayjs.utc(clickTimeRange.end_time).format("MM/DD/YYYY h:mm A") + " UTC" : "N/A"}</p>
                            </div>
                        </div>
                
                    ) : (
                        <p>Loading...</p>
                    )}
                </div>
            </div>

        </div>

        

        {questionAverage.length > 0 && (
            <>
                <h2>Measure-Level Averages</h2>
                <GroupedBarChart questionAverage={questionAverage} />
            </>
        )}
        
        <QuestionTable />
      
        </div>
    );
};

export default Overview;
